import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Loader2, Unlock } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { EncodingSelect } from "@/components/crypto/EncodingSelect";
import { KeyIvFields } from "@/components/crypto/KeyIvFields";
import { ResultBox } from "@/components/crypto/ResultBox";
import { validateKeyIv } from "@/components/crypto/validation";
import {
  aesDecrypt,
  BYTE_ENCODINGS,
  type OutputEncoding,
  OUTPUT_ENCODINGS,
} from "@/lib/crypto";

const schema = z
  .object({
    ciphertext: z.string().min(1, "Enter the encrypted data to decrypt."),
    ciphertextEncoding: z.enum(OUTPUT_ENCODINGS),
    key: z.string().min(1, "Key is required."),
    keyEncoding: z.enum(BYTE_ENCODINGS),
    iv: z.string().min(1, "IV is required."),
    ivEncoding: z.enum(BYTE_ENCODINGS),
  })
  .superRefine(validateKeyIv);

type DecryptValues = z.infer<typeof schema>;

export function DecryptForm() {
  const [result, setResult] = React.useState<string | null>(null);

  const form = useForm<DecryptValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      ciphertext: "",
      ciphertextEncoding: "base64",
      key: "",
      keyEncoding: "utf-8",
      iv: "",
      ivEncoding: "utf-8",
    },
  });

  const ciphertextEncoding = form.watch("ciphertextEncoding");

  async function onSubmit(values: DecryptValues) {
    try {
      const plaintext = await aesDecrypt(values);
      setResult(plaintext);
      toast.success("Data decrypted");
    } catch (e) {
      setResult(null);
      toast.error(e instanceof Error ? e.message : "Decryption failed");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <Unlock className="h-5 w-5" />
          Decrypt
        </CardTitle>
        <CardDescription>
          Enter the encrypted data with the same key and IV to recover the
          original text.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="ciphertext"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between gap-2">
                    <FormLabel>Encrypted data</FormLabel>
                    <EncodingSelect
                      ariaLabel="Encrypted data encoding"
                      value={ciphertextEncoding}
                      onValueChange={(v) =>
                        form.setValue(
                          "ciphertextEncoding",
                          v as OutputEncoding,
                          { shouldValidate: true },
                        )
                      }
                      options={OUTPUT_ENCODINGS}
                    />
                  </div>
                  <FormControl>
                    <Textarea
                      placeholder="Base64 or Hex ciphertext…"
                      className="min-h-[96px] font-mono"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <KeyIvFields />

            <Button
              type="submit"
              className="w-full"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Unlock className="h-4 w-4" />
              )}
              Decrypt
            </Button>
          </form>
        </Form>

        {result !== null && (
          <ResultBox
            label="Decrypted data (original)"
            value={result}
            className="mt-5"
          />
        )}
      </CardContent>
    </Card>
  );
}
